import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Package, Truck, Store, Check, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { money } from "../lib/helpers";
import { PageHeader, Field, NativeSelect, Empty } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Suppliers } from "../components/SupplierEditor";

const STATUSES = ["paid", "processing", "dispatched", "ready_for_collection", "completed", "cancelled"];
const BLANK = { name: "", description: "", category: "", sku: "", image: "", prices: {}, fulfillment: "own", supplier_id: "", delivery_allowed: true, pickup_allowed: true, stock: "", active: true };

function resize(file) {
  return new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, 600 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = img.width * s; c.height = img.height * s;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      res(c.toDataURL("image/jpeg", 0.8));
    };
    img.src = URL.createObjectURL(file);
  });
}

function ProductEditor({ open, onOpenChange, product, suppliers, currencies, onSaved }) {
  const [f, setF] = useState(BLANK);
  useEffect(() => { if (open) setF({ ...BLANK, ...(product || {}), supplier_id: product?.supplier_id || "", stock: product?.stock ?? "" }); }, [open, product]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    const body = { ...f, stock: f.stock === "" ? null : Number(f.stock), supplier_id: f.supplier_id || null, tax_rate: f.tax_rate === "" || f.tax_rate == null ? null : Number(f.tax_rate) };
    ["id", "created_at", "clinic_id"].forEach((k) => delete body[k]);
    try { product?.id ? await api.put(`/products/${product.id}`, body) : await api.post("/products", body); toast.success("Product saved"); onOpenChange(false); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="product-editor">
        <DialogHeader><DialogTitle>Product</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name"><Input data-testid="product-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Category"><Input data-testid="product-category-input" value={f.category} onChange={(e) => set("category", e.target.value)} /></Field>
          <Field label="Description" className="sm:col-span-2"><Textarea rows={3} data-testid="product-description-input" value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <Field label="Photo">
            <div className="flex items-center gap-3">
              {f.image && <img src={f.image} alt="" className="h-14 w-14 rounded object-cover border" />}
              <input type="file" accept="image/*" data-testid="product-image-input" className="text-xs" onChange={async (e) => e.target.files[0] && set("image", await resize(e.target.files[0]))} />
            </div>
          </Field>
          <Field label="Stock (empty = unlimited)"><Input type="number" data-testid="product-stock-input" value={f.stock} onChange={(e) => set("stock", e.target.value)} /></Field>
          {f.supplier_ref && <label className="flex items-center gap-2 text-sm sm:col-span-2" data-testid="product-price-locked"><Switch checked={!!f.price_locked} onCheckedChange={(v) => set("price_locked", v)} />Keep my price when the supplier syncs (their price {f.source_price}, my cost {f.cost})</label>}
          <Field label="VAT % (empty = location default)"><Input type="number" min={0} max={100} step="0.1" data-testid="product-vat-input" value={f.tax_rate ?? ""} onChange={(e) => set("tax_rate", e.target.value)} /></Field>
          <div className="sm:col-span-2 rounded-[var(--radius)] border p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <p className="col-span-full text-sm font-semibold">Prices (leave empty if not sold in that currency)</p>
            {currencies.map((c) => (
              <Field key={c} label={c}><Input type="number" step="0.01" data-testid={`product-price-${c}`} value={f.prices[c] ?? ""} onChange={(e) => set("prices", { ...f.prices, [c]: e.target.value })} /></Field>
            ))}
          </div>
          <Field label="Fulfilment">
            <NativeSelect testId="product-fulfillment-select" value={f.fulfillment} onChange={(v) => set("fulfillment", v)}
              options={[{ value: "own", label: "My stock (I dispatch / customer collects)" }, { value: "dropship", label: "Drop-ship by supplier" }]} />
          </Field>
          {f.fulfillment === "dropship" ? (
            <Field label="Supplier"><NativeSelect testId="product-supplier-select" value={f.supplier_id} onChange={(v) => set("supplier_id", v)} options={[{ value: "", label: "—" }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))]} /></Field>
          ) : (
            <div className="flex flex-col gap-2 text-sm pt-5">
              <label className="flex items-center gap-2"><Switch data-testid="product-delivery-switch" checked={f.delivery_allowed} onCheckedChange={(v) => set("delivery_allowed", v)} />Can be delivered</label>
              <label className="flex items-center gap-2"><Switch data-testid="product-pickup-switch" checked={f.pickup_allowed} onCheckedChange={(v) => set("pickup_allowed", v)} />Can be collected</label>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="product-active-switch" checked={f.active} onCheckedChange={(v) => set("active", v)} />Visible in shop</label>
        </div>
        <Button className="rounded-full" data-testid="product-save-btn" disabled={!f.name} onClick={save}>Save</Button>
      </DialogContent>
    </Dialog>
  );
}

function Orders() {
  const { user } = useApp();
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/orders").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const patch = async (o, status) => {
    const tracking = status === "dispatched" ? window.prompt("Tracking number (optional)", o.tracking || "") ?? "" : o.tracking;
    try { await api.patch(`/orders/${o.id}`, { status, tracking }); toast.success("Order updated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!list.length) return <Empty text="No orders yet" testId="orders-empty" />;
  return (
    <div className="space-y-3" data-testid="orders-list">
      {list.map((o) => (
        <div key={o.id} className="rounded-[var(--radius)] border bg-card p-5 grid md:grid-cols-[1fr_auto] gap-4" data-testid={`order-${o.number}`}>
          <div className="space-y-1 text-sm">
            <div className="flex flex-wrap items-center gap-3"><span className="font-mono font-semibold">{o.number}</span><span className="font-semibold">{o.client_name}</span>
              <span className="text-muted-foreground">{o.email}</span></div>
            <div>{o.items.map((i) => `${i.qty}× ${i.name}${i.fulfillment === "dropship" ? " (drop-ship)" : ""}`).join(", ")}</div>
            <div className="flex items-center gap-2 text-muted-foreground">{o.fulfillment === "pickup" ? <><Store className="h-3.5 w-3.5" />Collect at {o.pickup_location_name}</>
              : <><Truck className="h-3.5 w-3.5" />{Object.values(o.address).filter(Boolean).join(", ")}</>}</div>
            {o.supplier_notified_at && <div className="text-xs text-emerald-700">Supplier notified</div>}
            {(o.tracking || []).map((t, k) => <div key={k} className="text-xs" data-testid={`order-tracking-${o.number}-${k}`}>Tracking: <b>{t.tracking_number}</b> {t.carrier}</div>)}
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="font-display text-xl font-bold">{money(o.total, o.currency)}</span>
            <NativeSelect testId={`order-status-${o.number}`} value={o.status} className="h-9 text-xs" onChange={(v) => patch(o, v)}
              options={STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))} />
            {user && <span className="text-xs text-muted-foreground">{o.created_at.slice(0, 10)}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Approvals() {
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/shop/approvals").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const set = async (id, status) => { try { await api.post(`/clients/${id}/shop-access`, { status }); toast.success(`Access ${status}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!list.length) return <Empty text="No access requests yet" testId="approvals-empty" />;
  return (
    <div className="rounded-[var(--radius)] border bg-card divide-y" data-testid="approvals-list">
      {list.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`approval-${c.id}`}>
          <div className="flex-1 min-w-[180px]"><a href={`/app/clients/${c.id}`} className="font-semibold hover:underline">{c.first_name} {c.last_name}</a>
            <div className="text-xs text-muted-foreground">{c.email} · {c.phone} · {c.source === "shop" ? "Shop lead" : "Patient"}</div></div>
          <span className={`text-xs rounded-full px-2.5 py-1 ${c.shop_status === "approved" ? "bg-emerald-500/15 text-emerald-800" : c.shop_status === "rejected" ? "bg-red-500/15 text-red-700" : "bg-amber-500/15 text-amber-800"}`}>{c.shop_status}</span>
          {c.shop_status !== "approved" && <Button size="sm" className="rounded-full" data-testid={`approve-${c.id}`} onClick={() => set(c.id, "approved")}><Check className="h-4 w-4 mr-1" />Approve</Button>}
          {c.shop_status !== "rejected" && <Button size="sm" variant="ghost" data-testid={`reject-${c.id}`} onClick={() => set(c.id, "rejected")}><X className="h-4 w-4 mr-1" />{c.shop_status === "approved" ? "Revoke" : "Reject"}</Button>}
        </div>
      ))}
    </div>
  );
}

function ShopSettings({ currencies }) {
  const { locations } = useApp();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/shop/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => { try { setS((await api.put("/shop/settings", s)).data); toast.success("Shop settings saved"); } catch (e) { toast.error(errMsg(e)); } };
  const toggleLoc = (id) => setS({ ...s, pickup_location_ids: s.pickup_location_ids.includes(id) ? s.pickup_location_ids.filter((x) => x !== id) : [...s.pickup_location_ids, id] });
  return (
    <div className="max-w-2xl space-y-5">
      <label className="flex items-center gap-3 text-sm"><Switch data-testid="shop-enabled-switch" checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} />Shop open</label>
      <Field label="Welcome text shown in the shop"><Textarea data-testid="shop-intro-input" value={s.intro} onChange={(e) => setS({ ...s, intro: e.target.value })} /></Field>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {currencies.map((c) => <Field key={c} label={`Delivery fee ${c}`}><Input type="number" step="0.01" data-testid={`shop-fee-${c}`} value={s.shipping_fees[c] ?? ""} onChange={(e) => setS({ ...s, shipping_fees: { ...s.shipping_fees, [c]: e.target.value } })} /></Field>)}
      </div>
      <div><p className="text-xs text-muted-foreground mb-2">Collection points</p>
        <div className="flex flex-wrap gap-2">{locations.map((l) => (
          <button key={l.id} data-testid={`shop-pickup-${l.id}`} onClick={() => toggleLoc(l.id)} className={`rounded-full border px-3 py-1.5 text-xs ${s.pickup_location_ids.includes(l.id) ? "bg-primary text-primary-foreground" : ""}`}>{l.name}</button>
        ))}</div></div>
      <Button className="rounded-full" data-testid="shop-settings-save-btn" onClick={save}>Save</Button>
    </div>
  );
}

export default function Shop() {
  const { locations, settings } = useApp();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [edit, setEdit] = useState(null);
  const currencies = Array.from(new Set(locations.map((l) => l.currency)));
  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader eyebrow={`Shop link: ${window.location.origin}/shop/${settings.slug}`} title="Shop">
        <Button className="rounded-full" data-testid="new-product-btn" onClick={() => setEdit({})}>New product</Button>
      </PageHeader>
      <Tabs defaultValue="orders">
        <TabsList className="flex-wrap h-auto mb-6">
          {["orders", "products", "approvals", "suppliers", "settings"].map((k) => <TabsTrigger key={k} value={k} data-testid={`shop-tab-${k}`} className="capitalize">{k}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="orders"><Orders /></TabsContent>
        <TabsContent value="products">
          {!products.length && <Empty text="No products yet" />}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 stagger">
            {products.map((p) => (
              <button key={p.id} onClick={() => setEdit(p)} data-testid={`product-card-${p.id}`} className={`lift text-left rounded-[var(--radius)] border bg-card overflow-hidden ${p.active ? "" : "opacity-50"}`}>
                <div className="aspect-[4/3] bg-muted grid place-items-center">{p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <Package className="h-8 w-8 text-muted-foreground" />}</div>
                <div className="p-4 space-y-1">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.fulfillment === "dropship" ? "Drop-ship" : "Own stock"}{p.stock != null && ` · ${p.stock} in stock`}</div>
                  <div className="text-sm">{Object.entries(p.prices).map(([c, v]) => money(v, c)).join(" · ")}</div>
                </div>
              </button>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="approvals"><Approvals /></TabsContent>
        <TabsContent value="suppliers"><Suppliers list={suppliers} reload={load} /></TabsContent>
        <TabsContent value="settings"><ShopSettings currencies={currencies} /></TabsContent>
      </Tabs>
      <ProductEditor open={!!edit} onOpenChange={(o) => !o && setEdit(null)} product={edit} suppliers={suppliers} currencies={currencies} onSaved={load} />
    </div>
  );
}
